import { ApiFeeType } from 'api/rtk-api'
import { Currency } from 'utilities/currency'
import { sentenceCase } from 'utilities/string'

export interface Operation {
  feeType: ApiFeeType
  generatedDate: string
  state: 'Completed' | 'Processing'
  type: 'TriggeredFeeDeduction' | 'FeeDeduction'
  value: number
}

export interface FeeCategory {
  fees: number
  name: string
  split: { fees: number; name: string }[]
}

export interface FeeSplit {
  note: FeeCategory
  portfolio: FeeCategory
  strategy: FeeCategory
}

export interface FormattedFeeData {
  currency: Currency
  fees: number
  split: FeeSplit
}

export interface FormattedFeeDataWithDate extends FormattedFeeData {
  date: string
}

export function getFeeCategoryName(feeType: ApiFeeType) {
  switch (feeType) {
    case 'PlatformFee':
    case 'DistributionFee':
    case 'LifeCompanyFee':
      return 'Note'
    case 'PortfolioManagementFee':
      return 'Portfolio'
    default:
      return 'Strategy'
  }
}

export function getFeeTypeName(feeType: ApiFeeType) {
  switch (feeType) {
    case 'GapProtectionPremium':
      return 'Protection premium'
    case 'RiskControlledPremiumFee':
      return 'Risk controlled premium'
    case 'HedgingPremiumFee':
      return 'Hedging premium'
    case 'Delta1PremiumFee':
      return 'Delta one premium'
    default:
      return sentenceCase(feeType)
  }
}

export function formatFeeOperations(data: Operation[]): FeeSplit {
  const filteredFees = data.filter(
    op =>
      op.state === 'Completed' &&
      ['FeeDeduction', 'TriggeredFeeDeduction', 'ProtectedFeeDeduction'].includes(op.type)
  )
  const feeCategories: { [key: string]: FeeCategory } = {}

  for (const fee of filteredFees) {
    const feeCategoryName = getFeeCategoryName(fee.feeType)
    const feeCategory = feeCategories[feeCategoryName] || {
      name: feeCategoryName,
      fees: 0,
      split: []
    }

    feeCategory.fees += fee.value

    const feeTypeName = getFeeTypeName(fee.feeType)
    const feeType = feeCategory.split.find(ft => ft.name === feeTypeName) || {
      name: feeTypeName,
      fees: 0
    }

    feeType.fees += fee.value
    if (!feeCategory.split.includes(feeType)) {
      feeCategory.split.push(feeType)
    }

    feeCategories[feeCategoryName] = feeCategory
  }

  return {
    note: feeCategories.Note || {
      name: 'Note',
      fees: 0,
      split: []
    },
    portfolio: feeCategories.Portfolio || {
      name: 'Portfolio',
      fees: 0,
      split: []
    },
    strategy: feeCategories.Strategy || {
      name: 'Strategy',
      fees: 0,
      split: []
    }
  }
}
